import React from 'react';
import { Link } from 'react-router-dom';
import './Card.css';

/**
 * Card primitive — content surface with hairline border on cream
 * canvas. Replaces the legacy `.card` selector and the various
 * inline `box-shadow: 0 1px 3px ...` hand-rolls.
 *
 * Variants:
 *  - `default`   — canvas-white background, hairline border, no
 *                  hover affordance. Static content cards.
 *  - `hoverable` — hairline darkens to ink-muted on hover.
 *                  Use when the card is itself a link / button.
 *  - `feature`   — soft canvas tint (canvas-soft), no border.
 *                  Used for feature-list tiles where multiple
 *                  cards form a flat band.
 *
 * `as` polymorphism: pass `as="link"` + `to=""` for a Link card,
 * `as="article"` for semantic article wrap, default `<div>`.
 */
const Card = React.forwardRef(({
  variant = 'default',
  as = 'div',
  to,
  className = '',
  children,
  ...rest
}, ref) => {
  const classes = [
    'card-primitive',
    `card-primitive--${variant}`,
    className,
  ].filter(Boolean).join(' ');

  if (as === 'link' && to) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {children}
      </Link>
    );
  }

  const Tag = as === 'article' ? 'article' : 'div';
  return (
    <Tag ref={ref} className={classes} {...rest}>
      {children}
    </Tag>
  );
});

Card.displayName = 'Card';

export default Card;
